import { inject, Injectable } from '@angular/core';
import { getOpenTelemetryProviderAsync } from '../open-telemetry.facade';
import type { Span, OtelProvider } from '../open-telemetry.interfaces';
import type { ImageLoadStartPayload, NetworkRequestStartPayload } from './performance-metrics.interfaces';
import { PerformanceMetricsSpanManager } from './performance-metrics-span.manager';
import { PerformanceMetricCategory, PerformanceMetric } from './performance-metrics.interfaces';
import { determineImageCategory } from './image-metrics.utils';
import { DeviceIdService } from '@lentau/core/api/shared/device-id.service';
import { environment } from '@lentau/core/environments';

interface BufferedImageTracking {
  type: 'image';
  payload: ImageLoadStartPayload;
  startTime: number;
  span: Span | null;
}

interface NetworkCompletePayload {
  responseCode: number;
  responseSize?: number;
  duration: number;
  traceId?: string;
  endTime?: number;
}

interface BufferedNetworkTracking {
  type: 'network';
  payload: NetworkRequestStartPayload;
  startTime: number;
  span: Span | null;
  completePayload?: NetworkCompletePayload;
}

@Injectable({ providedIn: 'root' })
export class PerformanceMetricsBufferService {
  private readonly spanManager: PerformanceMetricsSpanManager = inject(PerformanceMetricsSpanManager);
  private readonly deviceIdService: DeviceIdService = inject(DeviceIdService);

  private imageTrackingBuffer: Map<string, BufferedImageTracking> = new Map();
  private networkTrackingBuffer: Map<string, BufferedNetworkTracking> = new Map();
  private isOtelInitialized: boolean = false;

  // Кэшируем deviceId и timeOrigin для избежания повторных вычислений
  private cachedDeviceId: string | null = null;
  private cachedTimeOrigin: number | null = null;

  public async initialize(): Promise<void> {
    if (this.isOtelInitialized) {
      return;
    }

    try {
      const provider: OtelProvider | null = await getOpenTelemetryProviderAsync();
      if (provider) {
        this.isOtelInitialized = true;
        await this.processBufferedTrackings();
      }
    } catch (error: unknown) {
      console.warn('[Performance Metrics Buffer]: Failed to initialize OTEL', error);
    }
  }

  public async startImageTracking(
    trackingId: string,
    payload: ImageLoadStartPayload
  ): Promise<{ span: Span | null; traceId?: string }> {
    const startTime: number = performance.now();
    const timeOrigin: number = this.getTimeOrigin();

    if (!this.isOtelInitialized) {
      // Буферизуем до инициализации OTEL
      this.imageTrackingBuffer.set(trackingId, {
        type: 'image',
        payload,
        startTime: timeOrigin + startTime,
        span: null,
      });
      return { span: null };
    }

    // Когда OTEL инициализирован, создаем спан и сохраняем в буфере для completeImageTracking
    const result: { span: Span | null; traceId?: string } = await this.createImageSpan(
      trackingId,
      payload,
      timeOrigin + startTime
    );
    this.imageTrackingBuffer.set(trackingId, {
      type: 'image',
      payload,
      startTime: timeOrigin + startTime,
      span: result.span,
    });
    return result;
  }

  public async completeImageTracking(
    trackingId: string,
    completePayload: {
      status: 'success' | 'error';
      duration: number;
      naturalWidth?: number;
      naturalHeight?: number;
      displayedWidth?: number;
      displayedHeight?: number;
      currentSrc?: string;
      regionName?: string;
      errorMessage?: string;
    }
  ): Promise<void> {
    const buffered: BufferedImageTracking | undefined = this.imageTrackingBuffer.get(trackingId);

    if (!buffered) {
      console.warn('[Performance Metrics Buffer]: Image tracking not found', trackingId);
      return;
    }

    const span: Span | null = buffered.span;

    if (!span) {
      // Если span еще не создан, создаем его сейчас
      const result: { span: Span | null; traceId?: string } = await this.createImageSpan(
        trackingId,
        buffered.payload,
        buffered.startTime
      );
      if (result.span) {
        buffered.span = result.span;
        await this.finalizeImageSpan(result.span, completePayload, buffered.startTime);
      }
    } else {
      await this.finalizeImageSpan(span, completePayload, buffered.startTime);
    }

    this.imageTrackingBuffer.delete(trackingId);
  }

  public async startNetworkTracking(
    trackingId: string,
    payload: NetworkRequestStartPayload
  ): Promise<{ span: Span | null; traceId?: string }> {
    // Используем точное время начала из payload, если оно передано
    // Иначе используем текущее время (fallback для обратной совместимости)
    const timeOrigin: number = this.getTimeOrigin();
    const startTimeAbsolute: number = payload.startTime ?? timeOrigin + performance.now();

    if (!this.isOtelInitialized) {
      // Буферизуем до инициализации OTEL
      this.networkTrackingBuffer.set(trackingId, {
        type: 'network',
        payload,
        startTime: startTimeAbsolute,
        span: null,
      });
      return { span: null };
    }

    // Если есть currentScreenName, но screen span еще не создан,
    // откладываем создание span до создания screen_render.cart
    // Это гарантирует, что сетевые запросы будут правильно прикрепляться к screen_render.cart
    const currentScreenName: string | null = this.spanManager.getCurrentScreenName();
    const hasScreenSpan: boolean = currentScreenName ? this.spanManager.hasScreenSpan(currentScreenName) : false;
    if (currentScreenName && !hasScreenSpan && this.isOtelInitialized) {
      // Буферизуем до создания screen span
      this.networkTrackingBuffer.set(trackingId, {
        type: 'network',
        payload,
        startTime: startTimeAbsolute,
        span: null,
      });
      return { span: null };
    }

    // Когда OTEL инициализирован, создаем спан и сохраняем в буфере для completeNetworkTracking
    const result: { span: Span | null; traceId?: string } = await this.createNetworkSpan(
      trackingId,
      payload,
      startTimeAbsolute
    );
    this.networkTrackingBuffer.set(trackingId, {
      type: 'network',
      payload,
      startTime: startTimeAbsolute,
      span: result.span,
    });
    return result;
  }

  public async completeNetworkTracking(trackingId: string, completePayload: NetworkCompletePayload): Promise<void> {
    const buffered: BufferedNetworkTracking | undefined = this.networkTrackingBuffer.get(trackingId);

    if (!buffered) {
      console.warn('[Performance Metrics Buffer]: Network tracking not found', trackingId);
      return;
    }

    const span: Span | null = buffered.span;

    if (!span) {
      // Если есть currentScreenName, но screen span еще не создан,
      // откладываем создание span до создания screen_render.cart
      // Это гарантирует, что сетевые запросы будут правильно прикрепляться к screen_render.cart
      // НЕ проверяем isOtelInitialized здесь, так как он может измениться между вызовами
      const currentScreenName: string | null = this.spanManager.getCurrentScreenName();
      const hasScreenSpan: boolean = currentScreenName ? this.spanManager.hasScreenSpan(currentScreenName) : false;
      if (currentScreenName && !hasScreenSpan) {
        // Буферизуем до создания screen span - не удаляем из буфера, оставляем для обработки позже
        // Обновляем буфер с completePayload для последующей обработки
        buffered.payload = { ...buffered.payload };
        buffered.completePayload = completePayload;
        return; // Не удаляем из буфера, оставляем для обработки позже
      }

      // Если span еще не создан, создаем его сейчас
      const result: { span: Span | null; traceId?: string } = await this.createNetworkSpan(
        trackingId,
        buffered.payload,
        buffered.startTime
      );
      if (result.span) {
        buffered.span = result.span;
        await this.finalizeNetworkSpan(result.span, buffered.payload, completePayload);
      }
    } else {
      await this.finalizeNetworkSpan(span, buffered.payload, completePayload);
    }

    this.networkTrackingBuffer.delete(trackingId);
  }

  private async processBufferedTrackings(): Promise<void> {
    // Обрабатываем буферизованные изображения
    for (const [trackingId, buffered] of this.imageTrackingBuffer.entries()) {
      if (!buffered.span) {
        const result: { span: Span | null; traceId?: string } = await this.createImageSpan(
          trackingId,
          buffered.payload,
          buffered.startTime
        );
        if (result.span) {
          buffered.span = result.span;
        }
      }
    }
  }

  public async processBufferedNetworkTrackings(): Promise<void> {
    // Обрабатываем буферизованные сетевые запросы
    // Это вызывается после создания screen span, чтобы гарантировать правильный родитель
    for (const [trackingId, buffered] of this.networkTrackingBuffer.entries()) {
      if (!buffered.span) {
        const result: { span: Span | null; traceId?: string } = await this.createNetworkSpan(
          trackingId,
          buffered.payload,
          buffered.startTime
        );
        if (result.span) {
          buffered.span = result.span;
          // Если есть completePayload, финализируем span сразу
          const completePayload: NetworkCompletePayload | undefined = buffered.completePayload;
          if (completePayload) {
            await this.finalizeNetworkSpan(result.span, buffered.payload, completePayload);
            this.networkTrackingBuffer.delete(trackingId);
          }
        }
      }
    }
  }

  private async createImageSpan(
    trackingId: string,
    payload: ImageLoadStartPayload,
    startTime: number
  ): Promise<{ span: Span | null; traceId?: string }> {
    const imageCategory: string = determineImageCategory(
      payload.imageCategory,
      payload.width,
      payload.height,
      payload.requestedSrc || payload.renderedSrc
    );

    const regionSlug: string | undefined = payload.regionSlug;

    const metric: PerformanceMetric = {
      category: PerformanceMetricCategory.IMAGE_LOAD,
      event: payload.componentName,
      attributes: {
        timestamp: Date.now(),
        device_id: this.getDeviceId(),
        platform: 'Web',
        version: environment.releaseVersion || 'unknown',
        duration: 0,
        'image.component_name': payload.componentName,
        'image.requested_src': payload.requestedSrc,
        'image.rendered_src': payload.renderedSrc,
        'image.width': payload.width,
        'image.height': payload.height,
        'image.priority': payload.priority,
        'image.image_category': imageCategory,
        'image.load_status': 'loading',
        'image.region_slug': regionSlug,
        'image.qa_id': payload.qaId,
        'image.alt': payload.alt,
        'image.screen': payload.screen,
      },
    };

    const span: Span | null = await this.spanManager.startSpanForMetric(metric, { startTime });

    if (span) {
      const traceId: string | undefined = span.spanContext().traceId;
      span.setAttribute('trace_id', traceId);
      return { span, traceId };
    }

    return { span: null };
  }

  private async finalizeImageSpan(
    span: Span,
    completePayload: {
      status: 'success' | 'error';
      duration: number;
      naturalWidth?: number;
      naturalHeight?: number;
      displayedWidth?: number;
      displayedHeight?: number;
      currentSrc?: string;
      regionName?: string;
      errorMessage?: string;
    },
    startTime: number
  ): Promise<void> {
    span.setAttribute('image.type', completePayload.status);
    span.setAttribute('image.duration_ms', completePayload.duration);

    if (completePayload.regionName) {
      span.setAttribute('image.region_name', completePayload.regionName);
    }

    if (completePayload.naturalWidth !== undefined) {
      span.setAttribute('image.natural_width', completePayload.naturalWidth);
    }
    if (completePayload.naturalHeight !== undefined) {
      span.setAttribute('image.natural_height', completePayload.naturalHeight);
    }
    if (completePayload.displayedWidth !== undefined) {
      span.setAttribute('image.displayed_width', completePayload.displayedWidth);
    }
    if (completePayload.displayedHeight !== undefined) {
      span.setAttribute('image.displayed_height', completePayload.displayedHeight);
    }
    if (completePayload.currentSrc) {
      span.setAttribute('image.current_src', completePayload.currentSrc);
    }
    if (completePayload.errorMessage) {
      span.setAttribute('image.error_message', completePayload.errorMessage);
    }

    // Используем startTime из буфера и добавляем duration для правильного расчета endTime
    // startTime уже в миллисекундах относительно timeOrigin
    const endTimeMs: number = startTime + completePayload.duration;

    span.end(endTimeMs);
  }

  private async createNetworkSpan(
    trackingId: string,
    payload: NetworkRequestStartPayload,
    startTime: number
  ): Promise<{ span: Span | null; traceId?: string }> {
    const metric: PerformanceMetric = {
      category: PerformanceMetricCategory.NETWORK,
      event: 'request',
      attributes: {
        timestamp: Date.now(),
        device_id: this.getDeviceId(),
        platform: 'Web',
        version: environment.releaseVersion || 'unknown',
        duration: 0,
        request_url: payload.requestUrl,
        http_method: payload.httpMethod,
        response_code: 0,
        request_size: payload.requestSize,
      },
    };

    const span: Span | null = await this.spanManager.startSpanForMetric(metric, { startTime });

    if (span) {
      const traceId: string | undefined = span.spanContext().traceId;
      span.setAttribute('trace_id', traceId);
      return { span, traceId };
    }

    return { span: null };
  }

  private async finalizeNetworkSpan(
    span: Span,
    startPayload: NetworkRequestStartPayload,
    completePayload: NetworkCompletePayload
  ): Promise<void> {
    span.setAttribute('response_code', completePayload.responseCode);
    span.setAttribute('duration_ms', completePayload.duration);

    if (completePayload.responseSize !== undefined) {
      span.setAttribute('response_size', completePayload.responseSize);
    }

    if (completePayload.traceId) {
      span.setAttribute('trace_id', completePayload.traceId);
    }

    // Используем точное время окончания из completePayload, если оно передано
    // Иначе вычисляем из текущего времени (fallback для обратной совместимости)
    const endTimeAbsolute: number = completePayload.endTime ?? this.getTimeOrigin() + performance.now();

    span.end(endTimeAbsolute);
  }

  /**
   * Получает и кэширует timeOrigin для избежания повторных вычислений
   */
  private getTimeOrigin(): number {
    if (this.cachedTimeOrigin === null) {
      this.cachedTimeOrigin = performance.timeOrigin ?? Date.now() - performance.now();
    }
    return this.cachedTimeOrigin;
  }

  /**
   * Получает и кэширует deviceId для избежания повторных вызовов сервиса
   */
  private getDeviceId(): string {
    if (this.cachedDeviceId === null) {
      this.cachedDeviceId = this.deviceIdService.getDeviceId();
    }
    return this.cachedDeviceId;
  }
}
